import { ITimeZone } from '../api/mylb/interfaces/itime-zone.interface';
import { toZonedTime } from 'date-fns-tz';

/**
 * Centralized timezone conversion utilities
 * Extracted from service layer to provide consistent timezone handling across the application
 */

/**
 * A calendar date with no time and no zone — just a label for "which day".
 *
 * Wall-clock values are carried as plain numbers rather than Date objects throughout this file.
 * A Date is an instant, and building one from local components silently rewrites any wall clock
 * that does not exist in the *browser's* zone: `new Date(2026, 2, 8, 2, 30)` in America/New_York
 * becomes 03:30, because 02:30 is inside that zone's spring-forward gap. That corrupts
 * conversions between two entirely unrelated zones, so no local Date is used as a carrier here.
 */
interface CalendarDate {
	y: number;
	mo: number; // 1-12
	d: number;
}

/** The calendar day a Date falls on, read in the browser's zone. */
function calendarDateOf(date: Date): CalendarDate {
	return { y: date.getFullYear(), mo: date.getMonth() + 1, d: date.getDate() };
}

/** Day arithmetic via UTC so it never lands in a local DST gap. */
function addDays(date: CalendarDate, days: number): CalendarDate {
	const shifted = new Date(Date.UTC(date.y, date.mo - 1, date.d + days));
	return { y: shifted.getUTCFullYear(), mo: shifted.getUTCMonth() + 1, d: shifted.getUTCDate() };
}

function weekdayOf(date: CalendarDate): number {
	return new Date(Date.UTC(date.y, date.mo - 1, date.d)).getUTCDay();
}

/** Whole days between two calendar dates. */
function daysBetween(from: CalendarDate, to: CalendarDate): number {
	const a = Date.UTC(from.y, from.mo - 1, from.d);
	const b = Date.UTC(to.y, to.mo - 1, to.d);
	return Math.round((b - a) / 86_400_000);
}

/** UTC offset in ms for an IANA zone at an instant, or null when the zone is unusable. */
function zoneOffsetMsAt(timeZoneName: string, at: Date): number | null {
	try {
		const parts = new Intl.DateTimeFormat('en-US', {
			timeZone: timeZoneName,
			hour12: false,
			year: 'numeric',
			month: '2-digit',
			day: '2-digit',
			hour: '2-digit',
			minute: '2-digit',
			second: '2-digit'
		}).formatToParts(at);

		const get = (type: string): number => Number(parts.find((p) => p.type === type)?.value);
		// Some engines render midnight as hour 24; normalise before rebuilding the instant.
		const asUtc = Date.UTC(get('year'), get('month') - 1, get('day'), get('hour') % 24, get('minute'), get('second'));
		if (Number.isNaN(asUtc)) return null;

		// asUtc is the zone's wall clock read as if it were UTC; the gap to the real instant is the offset.
		return Math.round((asUtc - at.getTime()) / 1000) * 1000;
	} catch {
		return null;
	}
}

/**
 * The instant at which `timeZoneName` reads the given wall clock.
 *
 * Two passes: guess the instant assuming the offset at the naive UTC reading, then re-resolve the
 * offset at that instant. The second pass is what makes DST transition weeks correct. Wall clocks
 * inside a spring-forward gap do not exist; they resolve to the instant just after the jump.
 */
function wallClockToInstant(timeZoneName: string, date: CalendarDate, hours: number, minutes: number): Date | null {
	const naiveUtc = Date.UTC(date.y, date.mo - 1, date.d, hours, minutes);
	const firstOffset = zoneOffsetMsAt(timeZoneName, new Date(naiveUtc));
	if (firstOffset === null) return null;

	const secondOffset = zoneOffsetMsAt(timeZoneName, new Date(naiveUtc - firstOffset));
	if (secondOffset === null) return null;

	return new Date(naiveUtc - secondOffset);
}

/** The wall clock `timeZoneName` shows at an instant. */
function instantToWallClock(timeZoneName: string, at: Date): { date: CalendarDate; hours: number; minutes: number } | null {
	try {
		const parts = new Intl.DateTimeFormat('en-US', {
			timeZone: timeZoneName,
			hour12: false,
			year: 'numeric',
			month: '2-digit',
			day: '2-digit',
			hour: '2-digit',
			minute: '2-digit'
		}).formatToParts(at);

		const get = (type: string): number => Number(parts.find((p) => p.type === type)?.value);
		const y = get('year');
		if (Number.isNaN(y)) return null;

		return { date: { y, mo: get('month'), d: get('day') }, hours: get('hour') % 24, minutes: get('minute') };
	} catch {
		return null;
	}
}

/**
 * UTC offset in hours for a timezone at a given instant.
 *
 * Resolved from the real IANA rules for `timeZoneName`, so DST is evaluated for the date in
 * question and for the zone in question. It is NOT derived from the browser's own DST state
 * (a Hong Kong browser would conclude nobody is on DST) and NOT from the stored
 * utcOffset/utcOffsetDST columns, which cannot express transition dates. Those columns are
 * used only when the IANA name is missing or unrecognised.
 */
export function getDSTAwareOffset(timezone: ITimeZone, at: Date = new Date()): number {
	if (timezone.timeZoneName) {
		const real = zoneOffsetMsAt(timezone.timeZoneName, at);
		if (real !== null) return real / 3_600_000;
	}
	return timezone.utcOffset || 0;
}

/**
 * Converts a recurring wall-clock time from one zone to another by anchoring it to a real
 * calendar date, so the correct DST rules apply. Returns the target wall clock plus the day
 * shift the conversion caused. Null when either zone lacks a usable IANA name.
 */
function convertWallClock(
	timeString: string,
	sourceTimezone: ITimeZone,
	targetTimezone: ITimeZone,
	anchor: CalendarDate
): { time: string; dayShift: number } | null {
	if (!sourceTimezone.timeZoneName || !targetTimezone.timeZoneName) return null;

	const [rawHours, rawMinutes] = timeString.split(':').map(Number);
	if (Number.isNaN(rawHours)) return null;
	const minutes = Number.isNaN(rawMinutes) ? 0 : rawMinutes;

	// 24:00 is an exclusive end-of-day marker: midnight on the following day.
	const isEndOfDay = rawHours === 24;
	const hours = isEndOfDay ? 0 : rawHours;
	const sourceDate = isEndOfDay ? addDays(anchor, 1) : anchor;

	const instant = wallClockToInstant(sourceTimezone.timeZoneName, sourceDate, hours, minutes);
	if (instant === null) return null;

	const target = instantToWallClock(targetTimezone.timeZoneName, instant);
	if (target === null) return null;

	const dayShift = daysBetween(sourceDate, target.date);
	const stillMidnight = target.hours === 0 && target.minutes === 0;

	// Preserve the 24:00 marker when the end of day survives the conversion as midnight.
	if (isEndOfDay && stillMidnight) {
		return { time: '24:00', dayShift };
	}

	return { time: `${two(target.hours)}:${two(target.minutes)}`, dayShift: dayShift + (isEndOfDay ? 1 : 0) };
}

/** First calendar date on or after `from` whose weekday matches `dayIndex` (0 = Sunday). */
function anchorDateForWeekday(dayIndex: number, from: CalendarDate): CalendarDate {
	return addDays(from, (dayIndex - weekdayOf(from) + 7) % 7);
}

/**
 * Converts a time string from one timezone to another.
 *
 * `referenceDate` anchors the conversion so DST is resolved against a real date. A recurring
 * weekly slot has no date of its own, so callers that care about a specific period (an
 * enrollment start date, a lesson date) should pass it; it defaults to today.
 */
export function convertTimeBetweenTimezones(
	timeString: string,
	sourceTimezone: ITimeZone,
	targetTimezone: ITimeZone,
	referenceDate: Date = new Date()
): string {
	// If timezones are the same, no conversion needed
	if (sourceTimezone.timeZoneID === targetTimezone.timeZoneID) {
		return timeString;
	}

	const converted = convertWallClock(timeString, sourceTimezone, targetTimezone, calendarDateOf(referenceDate));
	if (!converted) {
		console.warn('Timezone conversion unavailable; returning original time', {
			timeString,
			source: sourceTimezone.timeZoneName,
			target: targetTimezone.timeZoneName
		});
		return timeString;
	}

	return converted.time;
}

/**
 * Converts a day index when time conversion crosses midnight.
 *
 * The slot is anchored to the next real calendar date matching `dayIndex` (or to
 * `referenceDate`'s week) so the day shift reflects the actual offset on that date.
 */
export function convertDayBetweenTimezones(
	dayIndex: number,
	timeString: string,
	sourceTimezone: ITimeZone,
	targetTimezone: ITimeZone,
	referenceDate: Date = new Date()
): number {
	if (sourceTimezone.timeZoneID === targetTimezone.timeZoneID) {
		return dayIndex;
	}

	const anchor = anchorDateForWeekday(dayIndex, calendarDateOf(referenceDate));
	const converted = convertWallClock(timeString, sourceTimezone, targetTimezone, anchor);
	if (!converted) {
		return dayIndex;
	}

	return (dayIndex + converted.dayShift + 7) % 7;
}

/**
 * Formats a date according to timezone-aware settings
 */
export function formatDateInTimezone(date: Date, timezone: ITimeZone, format: 'short' | 'medium' | 'long' | 'time' = 'short'): string {
	// Hand the zone to Intl rather than shifting the epoch by a stored offset — Intl applies the
	// real DST rules for this instant, and shifting produces a Date whose components only read
	// correctly if the browser happens to sit at UTC.
	const base: Intl.DateTimeFormatOptions = timezone.timeZoneName ? { timeZone: timezone.timeZoneName } : {};

	switch (format) {
		case 'time':
			return date.toLocaleTimeString('en-US', {
				...base,
				hour: '2-digit',
				minute: '2-digit',
				hour12: true
			});
		case 'short':
			return date.toLocaleString('en-US', {
				...base,
				month: 'short',
				day: 'numeric',
				hour: '2-digit',
				minute: '2-digit'
			});
		case 'medium':
			return date.toLocaleString('en-US', {
				...base,
				month: 'long',
				day: 'numeric',
				year: 'numeric',
				hour: '2-digit',
				minute: '2-digit'
			});
		case 'long':
			return date.toLocaleString('en-US', {
				...base,
				weekday: 'long',
				month: 'long',
				day: 'numeric',
				year: 'numeric',
				hour: '2-digit',
				minute: '2-digit'
			});
		default:
			return date.toLocaleDateString('en-US', base);
	}
}

/**
 * Checks if a timezone conversion would result in a significant time difference
 * Used to determine when to show timezone context warnings
 */
export function hasSignificantTimezoneOffset(
	sourceTimezone: ITimeZone,
	targetTimezone: ITimeZone,
	thresholdHours: number = 2,
	at: Date = new Date()
): boolean {
	const sourceOffset = getDSTAwareOffset(sourceTimezone, at);
	const targetOffset = getDSTAwareOffset(targetTimezone, at);
	const offsetDifference = Math.abs(targetOffset - sourceOffset);

	return offsetDifference >= thresholdHours;
}

/**
 * Gets the timezone abbreviation with fallback
 */
export function getTimezoneAbbreviation(timezone: ITimeZone): string {
	return timezone.abbreviation || timezone.timeZoneName || 'UTC';
}

/**
 * Shifts a date by the offset difference between two zones, evaluated on that date.
 *
 * @deprecated Prefer convertDateTimeOffsetToTimezone / convertTimeBetweenTimezones. An instant
 * does not change when you view it from another zone, so shifting one is almost always a sign
 * that wall-clock and instant are being conflated.
 */
export function handleDSTTransition(date: Date, sourceTimezone: ITimeZone, targetTimezone: ITimeZone): Date {
	const sourceOffset = getDSTAwareOffset(sourceTimezone, date);
	const targetOffset = getDSTAwareOffset(targetTimezone, date);
	const offsetDifference = (targetOffset - sourceOffset) * 60 * 60 * 1000;

	return new Date(date.getTime() + offsetDifference);
}

/**
 * Converts time ranges between timezones
 */
export interface TimeRange {
	startTime: string;
	endTime: string;
	dayIndex: number;
}

export function convertTimeRangesBetweenTimezones(ranges: TimeRange[], sourceTimezone: ITimeZone, targetTimezone: ITimeZone): TimeRange[] {
	return ranges.map((range) => ({
		startTime: convertTimeBetweenTimezones(range.startTime, sourceTimezone, targetTimezone),
		endTime: convertTimeBetweenTimezones(range.endTime, sourceTimezone, targetTimezone),
		dayIndex: convertDayBetweenTimezones(range.dayIndex, range.startTime, sourceTimezone, targetTimezone)
	}));
}

/**
 * Utility for converting datetimeoffset values to user timezone
 * Specifically for lesson data that uses datetimeoffset
 */
export function convertDateTimeOffsetToTimezone(datetimeoffset: Date | string, targetTimezone: ITimeZone): Date {
	const date = typeof datetimeoffset === 'string' ? new Date(datetimeoffset) : datetimeoffset;

	// No early return for UTC or for zones whose stored offset happens to be 0: that shortcut handed
	// back the raw instant, whose local components are the *browser's* wall clock, not the target's.
	if (!targetTimezone.timeZoneName) {
		return new Date(date);
	}

	try {
		// Returns a Date whose local components are the target zone's wall clock — the contract the
		// callers rely on when they render with a plain date pipe.
		return toZonedTime(date, targetTimezone.timeZoneName);
	} catch (error) {
		console.warn('Failed to convert to timezone; returning the original instant:', error);
		return new Date(date);
	}
}

/**
 * Helper to determine if data uses timezone-aware storage
 */
export function isTimezoneAwareData(data: { timezoneID?: number }): boolean {
	return data.timezoneID !== undefined && data.timezoneID !== null;
}

/**
 * Gets timezone by ID from available timezones with fallback
 */
export function getTimezoneByID(timezoneID: number, availableTimezones: ITimeZone[]): ITimeZone | null {
	return availableTimezones.find((tz) => tz.timeZoneID === timezoneID) || null;
}

/**
 * Creates a timezone-aware context message for UI display
 */
export function createTimezoneContextMessage(displayTimezone: ITimeZone, originalTimezone?: ITimeZone, isConverted: boolean = false): string {
	const displayAbbr = getTimezoneAbbreviation(displayTimezone);

	if (!originalTimezone || !isConverted) {
		return `Times shown in ${displayAbbr}`;
	}

	const originalAbbr = getTimezoneAbbreviation(originalTimezone);
	if (hasSignificantTimezoneOffset(originalTimezone, displayTimezone)) {
		return `Times converted from ${originalAbbr} to ${displayAbbr}`;
	}

	return `Times shown in ${displayAbbr}`;
}

/**
 * Computes a wall-clock date range aligned to full days in the selected timezone.
 * Returns startOfDay and endOfDay Date objects representing the time span to request when backend expects raw Date values interpreted as local server (UTC-safe not enforced).
 * NOTE: This does NOT convert to UTC; it preserves the day boundaries of the user-selected timezone for direct use.
 */
export function computeTimezoneDayRange(start: Date, end: Date, tz: ITimeZone | null): { tzStart: Date; tzEnd: Date } {
	if (!tz) return { tzStart: startOfDaySafe(start), tzEnd: endOfDaySafe(end) };
	// We treat provided dates as already representing the intended wall-clock in selected timezone.
	return { tzStart: startOfDaySafe(start), tzEnd: endOfDaySafe(end) };
}

/** Lightweight startOfDay without importing date-fns to keep this util dependency-light */
function startOfDaySafe(d: Date): Date {
	return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0);
}
function endOfDaySafe(d: Date): Date {
	return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999);
}

/**
 * Converts a lesson (datetimeoffset stored) Date to selected timezone wall-clock using existing convertDateTimeOffsetToTimezone.
 * Provided to standardize naming & future migration to robust lib.
 */
export function convertLessonDateForDisplay(date: Date | string, tz: ITimeZone | null): Date {
	if (!tz) return new Date(date);
	return convertDateTimeOffsetToTimezone(date, tz);
}

/**
 * Today's calendar date, 'YYYY-MM-DD', as it reads right now in the given timezone. For seeding
 * date inputs: `new Date().toISOString().substring(0, 10)` is the UTC date, which is already
 * tomorrow during a US evening. Null timezone falls back to the browser's local date.
 */
export function todayInTimezone(tz: ITimeZone | null): string {
	// en-CA is the locale whose date format is YYYY-MM-DD; timeZoneName is the IANA name Intl expects.
	return new Intl.DateTimeFormat('en-CA', tz ? { timeZone: tz.timeZoneName } : {}).format(new Date());
}

/**
 * Returns dayIndex and HH:mm for a lesson start in selected timezone (used for conflict detection / grouping).
 */
export function lessonKeyInTimezone(lessonStart: Date, tz: ITimeZone | null): { dayIndex: number; startTime: string } {
	if (!tz) {
		return { dayIndex: lessonStart.getDay(), startTime: two(lessonStart.getHours()) + ':' + two(lessonStart.getMinutes()) };
	}
	const shifted = convertLessonDateForDisplay(lessonStart, tz);
	return { dayIndex: shifted.getDay(), startTime: two(shifted.getHours()) + ':' + two(shifted.getMinutes()) };
}

/**
 * Parse a date value respecting an origin (source) timezone so the wall-clock components are preserved.
 * Falls back gracefully if Intl fails or timezone metadata missing.
 */
function parseDateInTimezone(dateValue: Date | string, sourceTz: ITimeZone | null): Date {
	if (!sourceTz) return new Date(dateValue as any);
	const valueAsDate = typeof dateValue === 'string' ? new Date(dateValue) : dateValue;
	try {
		if (sourceTz.timeZoneName) {
			const formatter = new Intl.DateTimeFormat('en-CA', {
				timeZone: sourceTz.timeZoneName,
				year: 'numeric',
				month: '2-digit',
				day: '2-digit',
				hour: '2-digit',
				minute: '2-digit',
				second: '2-digit',
				hour12: false
			});
			const parts = formatter.formatToParts(valueAsDate);
			const part = (t: string) => parseInt(parts.find((p) => p.type === t)?.value || '0');
			return new Date(part('year'), part('month') - 1, part('day'), part('hour'), part('minute'), part('second'));
		}
	} catch {
		/* ignore and fallback */
	}
	// Fallback: if string without offset, attempt manual regex parse
	if (typeof dateValue === 'string') {
		const match = dateValue.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})/);
		if (match) {
			const [, y, m, d, hh, mm, ss] = match;
			return new Date(parseInt(y), parseInt(m) - 1, parseInt(d), parseInt(hh), parseInt(mm), parseInt(ss));
		}
	}
	return new Date(valueAsDate);
}

/**
 * Generic conversion of a wall-clock date from source timezone to target timezone keeping the instant consistent.
 */
function convertDateBetweenTimezones(dateValue: Date | string, sourceTz: ITimeZone | null, targetTz: ITimeZone | null): Date {
	if (!sourceTz || !targetTz || sourceTz.timeZoneID === targetTz.timeZoneID) {
		return parseDateInTimezone(dateValue, sourceTz);
	}
	const sourceDate = parseDateInTimezone(dateValue, sourceTz);
	try {
		const formatter = new Intl.DateTimeFormat('en-CA', {
			timeZone: targetTz.timeZoneName,
			year: 'numeric',
			month: '2-digit',
			day: '2-digit',
			hour: '2-digit',
			minute: '2-digit',
			second: '2-digit',
			hour12: false
		});
		const parts = formatter.formatToParts(sourceDate);
		const part = (t: string) => parseInt(parts.find((p) => p.type === t)?.value || '0');
		return new Date(part('year'), part('month') - 1, part('day'), part('hour'), part('minute'), part('second'));
	} catch {
		// Fallback: offsets resolved on the date in question, not from browser DST state
		const sourceOffset = getDSTAwareOffset(sourceTz, sourceDate);
		const targetOffset = getDSTAwareOffset(targetTz, sourceDate);
		const diffMs = (targetOffset - sourceOffset) * 60 * 60 * 1000;
		return new Date(sourceDate.getTime() + diffMs);
	}
}

/**
 * Converts instructor availability date (originating in instructor timezone) to the user-selected timezone for display.
 */
export function convertAvailabilityDateForDisplay(dateValue: Date | string, instructorTz: ITimeZone | null, selectedTz: ITimeZone | null): Date {
	return convertDateBetweenTimezones(dateValue, instructorTz, selectedTz);
}

/**
 * Converts a user-input date and time (in selected timezone) to a UTC Date object suitable for API submission.
 *
 * @param dateString The date string in yyyy-MM-dd format
 * @param timeString The time string in HH:mm format
 * @param selectedTimezone The timezone the user is viewing/inputting in
 * @returns A Date object representing the correct UTC instant
 *
 * @example
 * // User selects "2024-12-01" at "14:00" while viewing in America/New_York (EST)
 * // This should convert to UTC: "2024-12-01T19:00:00Z"
 * const utcDate = convertUserInputToUTC('2024-12-01', '14:00', newYorkTimezone);
 */
export function convertUserInputToUTC(dateString: string, timeString: string, selectedTimezone: ITimeZone | null): Date {
	// Fallback: if no timezone provided, parse as local browser time
	if (!selectedTimezone || !selectedTimezone.timeZoneName) {
		return new Date(`${dateString}T${timeString}:00`);
	}

	try {
		// Parse the date and time components
		const [year, month, day] = dateString.split('-').map(Number);
		const [hours, minutes] = timeString.split(':').map(Number);

		// Resolve the instant arithmetically rather than via a local Date carrier: a wall clock
		// inside the *browser's* spring-forward gap would be silently rewritten on construction,
		// producing an instant an hour off for input in an unrelated timezone.
		const utcDate = wallClockToInstant(selectedTimezone.timeZoneName, { y: year, mo: month, d: day }, hours, minutes);
		if (utcDate === null) {
			throw new Error(`Unrecognised timezone: ${selectedTimezone.timeZoneName}`);
		}

		return utcDate;
	} catch (error) {
		console.error('Error converting user input to UTC:', error);
		// Fallback: parse as local browser time
		return new Date(`${dateString}T${timeString}:00`);
	}
}

function two(n: number): string {
	return n.toString().padStart(2, '0');
}
